import { HttpErrorResponse } from '@angular/common/http'
import { Injectable, signal, computed } from '@angular/core'

import { Attribute } from '../../attributes'
import {
  View,
  ViewData,
  ViewExternalData,
  ViewFilter,
  ViewTarget,
} from '../view.model'
import { ViewsService } from '../views.service'
import { ViewsRepository } from '../views.repository'
import { VIEW_DEFAULT_CODE } from '../view.const'
import { defaultIfEmpty, firstValueFrom } from 'rxjs'

interface InitData {
  target: ViewTarget
  type: string
  userId: string
  viewId?: string
}

interface ViewsManagementState {
  viewId?: string
  userId?: string
  target?: ViewTarget
  type?: string
  view?: View
  views?: View[]
  attributes?: Attribute[]
  extData?: ViewExternalData[]
  error?: HttpErrorResponse
  showSharedViews: boolean
  isLoading: boolean
  isInitialized: boolean
  isModified: boolean
}

const VIEWS_MANAGEMENT_INITIAL_STATE: ViewsManagementState = {
  showSharedViews: false,
  isLoading: false,
  isInitialized: false,
  isModified: false,
}

@Injectable()
export class ViewsManagementStore {
  constructor(
    private viewsRepository: ViewsRepository,
    private viewsService: ViewsService,
  ) {}

  /**
   * Transform filter values from object format to string format for BETWEEN operator
   * Converts { min: 88, max: 100 } to "88|100"
   */
  private transformFiltersForSave(view: View): View {
    if (!view.filters) {
      return view
    }

    const transformedFilters = view.filters.map((filter) => {
      if (filter.value && typeof filter.value === 'object') {
        const valueObj = filter.value as { min?: number; max?: number }
        if (valueObj.min !== undefined && valueObj.max !== undefined) {
          return {
            ...filter,
            value: `${valueObj.min}|${valueObj.max}`,
          }
        }
      }
      return filter
    })

    return {
      ...view,
      filters: transformedFilters,
    }
  }

  private state = signal<ViewsManagementState>(VIEWS_MANAGEMENT_INITIAL_STATE)

  // Computed selectors
  readonly viewId = computed(() => this.state().viewId)
  readonly userId = computed(() => this.state().userId)
  readonly target = computed(() => this.state().target)
  readonly type = computed(() => this.state().type)
  readonly view = computed(() => this.state().view)
  readonly views = computed(() => this.state().views)
  readonly filters = computed(() => this.state().view?.filters)
  readonly attributes = computed(() => this.state().attributes)
  readonly extData = computed(() => this.state().extData)
  readonly error = computed(() => this.state().error)
  readonly paramsData = computed(() => ({
    params: this.state().view?.params,
    extData:
      this.state().isInitialized && this.state().view
        ? this.state().extData
        : undefined,
  }))
  readonly showSharedViews = computed(() => this.state().showSharedViews)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isModified = computed(() => this.state().isModified)
  readonly isEditable = computed(
    () => this.state().userId === this.state().view?.createdBy,
  )

  // Effects
  async init(initData: InitData): Promise<void> {
    if (!this.isLoading()) {
      this.setInit(initData)
      const showSharedViews = this.showSharedViews()

      try {
        const data = await firstValueFrom(
          this.viewsRepository
            .loadViewsData$(
              initData.viewId,
              initData.target,
              initData.userId,
              initData.type,
              showSharedViews,
            )
            .pipe(defaultIfEmpty(undefined)),
        )
        if (data) {
          this.setData(data)
          if (
            !data.views.find((v) => v._id === initData.viewId) &&
            data.views.length
          ) {
            this.setView(data.views[0])
          }
        } else {
          this.setIsLoading(false)
        }
      } catch (error) {
        this.setError(error as HttpErrorResponse)
      }
    }
  }

  async load(viewId?: string): Promise<void> {
    this.setIsLoading(true)

    const target = this.target()
    const type = this.type()

    if (!target || !type) {
      return
    }

    try {
      const data = await firstValueFrom(
        this.viewsRepository
          .loadViewData$(viewId, target, type)
          .pipe(defaultIfEmpty(undefined)),
      )

      if (data) {
        this.setViewData(data, viewId)
      } else {
        this.setIsLoading(false)
      }
    } catch (error) {
      this.setError(error as HttpErrorResponse)
    }
  }

  async showHideSharedViews(showHide: boolean): Promise<void> {
    this.setIsLoading(true)

    const viewId = this.viewId()
    const target = this.target()
    const type = this.type()
    const userId = this.userId()

    if (!target || !type || !userId) {
      return
    }

    try {
      const data = await firstValueFrom(
        this.viewsRepository
          .loadViewsData$(viewId, target, userId, type, showHide)
          .pipe(defaultIfEmpty(undefined)),
      )

      if (data) {
        this.setData(data, showHide)
        if (!data.views.find((v) => v._id === viewId) && data.views.length) {
          this.setView(data.views[0])
        }
      } else {
        this.setIsLoading(false)
      }
    } catch (error) {
      this.setError(error as HttpErrorResponse)
    }
  }

  async create(name: string): Promise<void> {
    this.setIsLoading(true)
    const view = this.view()

    if (!view) {
      return
    }

    try {
      // Transform filter values before creating
      const viewToCreate = this.transformFiltersForSave({ ...view, name })
      const newView = await firstValueFrom(
        this.viewsService.create$(viewToCreate).pipe(defaultIfEmpty(undefined)),
      )

      if (newView) {
        this.setNewView(newView)
      } else {
        this.setIsLoading(false)
      }
    } catch (error) {
      this.setError(error as HttpErrorResponse)
    }
  }

  async update(viewId: string): Promise<void> {
    this.unsetError()

    const target = this.target()
    const userId = this.userId()
    const type = this.type()
    const showSharedViews = this.showSharedViews()

    if (!userId || !target || !type) {
      return
    }

    try {
      const data = await firstValueFrom(
        this.viewsRepository
          .loadViewsData$(viewId, target, userId, type, showSharedViews)
          .pipe(defaultIfEmpty(undefined)),
      )

      if (data) {
        this.upsertView(data)
      } else {
        this.setIsLoading(false)
      }
    } catch (error) {
      this.setError(error as HttpErrorResponse)
    }
  }

  async save(): Promise<void> {
    this.setIsLoading(true)
    const view = this.view()
    if (!view) {
      return
    }

    try {
      // Transform filter values before saving
      const viewToSave = this.transformFiltersForSave(view)
      const updatedView = await firstValueFrom(
        this.viewsService.upsert$(viewToSave).pipe(defaultIfEmpty(undefined)),
      )

      if (updatedView) {
        this.setView(updatedView)
      } else {
        this.setIsLoading(false)
      }
    } catch (error) {
      this.setError(error as HttpErrorResponse)
    }
  }

  // State update methods

  setData(data: { viewData: ViewData; views: View[] }, showHide?: boolean) {
    this.state.set({
      ...this.state(),
      views: data.views,
      view: this.transformFiltersForLoad(data.viewData.view),
      attributes: data.viewData.attributes,
      extData: data.viewData.externalData,
      showSharedViews:
        showHide !== undefined ? showHide : this.showSharedViews(),
      isModified: false,
      isInitialized: true,
      isLoading: false,
    })
  }

  setViewData(viewData: ViewData, viewId?: string) {
    this.state.set({
      ...this.state(),
      view: this.transformFiltersForLoad(viewData.view),
      viewId,
      attributes: viewData.attributes,
      extData: viewData.externalData,
      isModified: false,
      isLoading: false,
    })
  }

  setInit(initData: InitData) {
    this.state.set({
      ...this.state(),
      target: initData.target,
      type: initData.type,
      userId: initData.userId,
      viewId: initData.viewId || VIEW_DEFAULT_CODE,
      isModified: false,
      isInitialized: false,
      isLoading: true,
    })
  }

  setIsLoading(isLoading: boolean): void {
    this.state.set({
      ...this.state(),
      isLoading: isLoading,
    })
  }

  setError(error: HttpErrorResponse): void {
    this.state.set({
      ...this.state(),
      error: error,
      isLoading: false,
    })
  }

  setView(view: View): void {
    this.state.set({
      ...this.state(),
      viewId: view._id,
      view: this.transformFiltersForLoad(view),
      isModified: false,
      isLoading: false,
    })
  }

  setNewView(newView: View): void {
    this.state.set({
      ...this.state(),
      viewId: newView._id,
      view: this.transformFiltersForLoad(newView),
      views: [...(this.state().views || []), newView],
      isModified: false,
      isLoading: false,
    })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  updateView(view: View): void {
    this.state.set({
      ...this.state(),
      view: this.transformFiltersForLoad(view),
      isModified: true,
      isLoading: false,
    })
  }

  upsertView(data: { viewData: ViewData; views: View[] }): void {
    this.state.set({
      ...this.state(),
      views: data.views,
      view: this.transformFiltersForLoad(data.viewData.view),
      attributes: data.viewData.attributes,
      extData: data.viewData.externalData,
      isInitialized: true,
      isModified: false,
      isLoading: false,
    })
  }

  setShowSharedViews(showSharedViews: boolean) {
    this.state.set({ ...this.state(), showSharedViews })
  }

  addAttribute(attribute: Attribute): void {
    this.state.set({
      ...this.state(),
      attributes: [...(this.state().attributes || []), attribute],
      isModified: true,
      isLoading: false,
    })
  }

  addExternalData(extData: ViewExternalData): void {
    this.state.set({
      ...this.state(),
      extData: [...(this.state().extData || []), extData],
      isModified: true,
      isLoading: false,
    })
  }

  setFilters(filters: ViewFilter[] | undefined): void {
    const currentView = this.view()
    if (currentView) {
      this.state.set({
        ...this.state(),
        view: { ...this.transformFiltersForLoad(currentView), filters },
        isModified: true,
        isLoading: false,
      })
    }
  }

  /**
   * Transform filter values from string format back to object format for BETWEEN operator.
   * Parses "min|max" (from API) into { min, max } so UI and viewFilterParams work.
   */
  private transformFiltersForLoad(view: View): View {
    if (!view.filters) {
      return view
    }

    const normalizedFilters = view.filters.map((filter) => {
      if (typeof filter.value !== 'string' || !filter.value.includes('|')) {
        return filter
      }

      const parts = filter.value.split('|').map((v) => v.trim())
      if (parts.length !== 2) {
        return filter
      }

      const [minVal, maxVal] = parts
      const minNum = Number(minVal)
      const maxNum = Number(maxVal)
      const value =
        Number.isNaN(minNum) || Number.isNaN(maxNum)
          ? { min: minVal, max: maxVal }
          : { min: minNum, max: maxNum }

      return {
        ...filter,
        value,
      }
    })

    return {
      ...view,
      filters: normalizedFilters,
    }
  }
}
